import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Select, MenuItem, Collapse } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useNavigate } from 'react-router-dom';
import KenButton from '../../../../../../GlobalComponents/KenButton';
import KenLoader from '../../../../../../GlobalComponents/KenLoader';
import routes from '../../../../../../Utils/routes';
import NewActivityDialog from './NewActivityDialog';
import {
    fetchFacultySubmissions,
    FacultyCourse,
    FacultyActivityItem,
    FacultySubmissionRow,
    FacultyFormItem,
} from './FacultyActivities';
import { fetchFacultyActivitiesData, invalidateFacultyActivitiesCache } from './FacultyActivitiesData';
import { fetchActivitySubmissionFile, fetchActivityReferenceFile, reviewSubmission } from '../../../Helper/ActivitiesHelper';
import { fetchAssessmentFormDetail, downloadFormResponsesCsv, AssessmentFormDetail } from '../../../Helper/AssessmentFormsHelper';
import { getUserDetails } from '../../../../../../storageService';
import { enqueueSnackbar } from 'notistack';
import FilePreviewDialog, { useFilePreview } from '../../../../../../GlobalComponents/FilePreviewDialog';

const useStyles = makeStyles((theme: any) => ({
    card: {
        background: theme.palette.KenColors.CttCard ?? '#fff',
        borderRadius: '14px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(20, 40, 92, 0.06)',
    },
    sectionHeaderBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '12px',
        flexWrap: 'wrap',
    },
    courseSelect: {
        borderRadius: '10px',
        fontSize: '14px',
        minWidth: '180px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.KenColors.CttBorder ?? '#ECEFF5',
        },
    },
    createBtn: {
        borderRadius: '10px',
        textTransform: 'none',
        fontWeight: 600,
    },
    hint: {
        fontSize: '13px',
        color: '#8A94A4',
        marginBottom: '16px',
    },
    infobox: {
        background: '#E9F2FB',
        borderRadius: '14px',
        padding: '14px 18px',
        display: 'flex',
        gap: '13px',
        alignItems: 'flex-start',
        marginBottom: '20px',
    },
    activityCard: {
        border: `1px solid ${theme.palette.KenColors.CttBorder ?? '#ECEFF5'}`,
        borderRadius: '14px',
        overflow: 'hidden',
    },
    activityList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    activityHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        cursor: 'pointer',
        gap: '14px',
        '&:hover': {
            background: '#F9FAFB',
        },
    },
    activityIcon: {
        width: 34,
        height: 34,
        borderRadius: '9px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        background: '#FBF3DE',
        color: '#CE9526',
    },
    statusPill: {
        padding: '4px 12px',
        borderRadius: '16px',
        fontSize: '13px',
        fontWeight: 500,
        display: 'inline-block',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    th: {
        textAlign: 'left',
        padding: '8px 16px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#8A94A4',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        borderBottom: '1px solid #E5E7EB',
    },
    td: {
        padding: '14px 16px',
        fontSize: '14px',
        borderBottom: '1px solid #F3F4F6',
    },
    tabs: {
        display: 'flex',
        gap: '32px',
        borderBottom: '1px solid #E5E7EB',
        marginBottom: '16px',
    },
    tab: {
        padding: '8px 0 16px',
        cursor: 'pointer',
        color: '#8A94A4',
        fontSize: '14px',
        fontWeight: 500,
        position: 'relative',
        background: 'none',
        border: 'none',
        fontFamily: 'inherit',
        '&.active': {
            color: '#212B38',
            fontWeight: 600,
            '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -1,
                left: 0,
                right: 0,
                height: '2px',
                background: theme.palette.KenColors.CttPrimary ?? '#1B7EC2',
            },
        },
    },
    formPill: {
        padding: '4px 12px',
        borderRadius: '16px',
        fontSize: '13px',
        fontWeight: 500,
        display: 'inline-block',
    },
    // Per-learner row pill — fully rounded and a touch bolder than the
    // form-level pills it sits below.
    respondedPill: {
        padding: '6px 16px',
        borderRadius: '999px',
        fontSize: '13px',
        fontWeight: 600,
        display: 'inline-block',
        whiteSpace: 'nowrap',
        background: '#E7F6EC',
        color: '#2E8B57',
    },
}));

function submissionStatusPillStyle(status: string) {
    if (status === 'Approved') return { background: '#DCFCE7', color: '#166534' };
    if (status === 'Rejected') return { background: '#FDE2E1', color: '#B3261E' };
    if (status === 'Reviewed') return { background: '#E9F2FB', color: '#2380C4' };
    return { background: '#FBF3DE', color: '#CE9526' };
}

function formPillStyle(formType: string) {
    if (/post/i.test(formType)) return { background: '#E8F5EC', color: '#166534' };
    return { background: '#E9F2FB', color: '#2380C4' };
}

function publishedPillStyle(published: boolean) {
    if (published) return { background: '#DCFCE7', color: '#166534' };
    return { background: '#F3F4F6', color: '#6B7280' };
}

interface FacultyActivitiesListProps {
    // When provided (embedded inside a single course's detail page), the
    // batch picker/dropdown is hidden and everything is scoped to this
    // course offering only.
    courseOfferingId?: string;
    // When provided, render only this section. The course detail page uses
    // this to keep Activities, Pre-test and Post-test as separate top-level tabs.
    section?: 'activities' | 'pre' | 'post';
}

// "View response" — a quiet secondary action: pill-shaped, muted grey text on
// a light border, so a long list of them stays calm rather than a wall of
// blue outlined buttons.
const viewResponseButtonSx = {
    textTransform: 'none',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    fontSize: '13px',
    fontWeight: 500,
    padding: '6px 18px',
    color: '#6B7280',
    borderColor: '#E5EAF2',
    backgroundColor: '#fff',
    '& .MuiButton-startIcon': { marginRight: '7px', color: '#9AA4B5' },
    '&:hover': { borderColor: '#CBD6E4', backgroundColor: '#F8FAFC' },
};

const FacultyActivitiesList: React.FC<FacultyActivitiesListProps> = ({ courseOfferingId, section }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const isScoped = !!courseOfferingId;
    const [activeTab, setActiveTab] = useState<'activities' | 'pre' | 'post'>(section || 'activities');
    const [loading, setLoading] = useState(true);
    const [courses, setCourses] = useState<FacultyCourse[]>([]);
    // Training (CourseName) narrows which batches the batch dropdown offers
    // — a faculty member teaching the same course across multiple batches
    // (terms) picks the training first, then the specific batch. Training
    // has no "all" option (always resolves to one real training); Batch
    // keeps "All batches" as an option and as its default.
    const [selectedCourseName, setSelectedCourseName] = useState<string>('');
    const [selectedCourseOfferingId, setSelectedCourseOfferingId] = useState<string>(courseOfferingId || 'all');
    const [activities, setActivities] = useState<FacultyActivityItem[]>([]);
    const [forms, setForms] = useState<FacultyFormItem[]>([]);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [submissions, setSubmissions] = useState<Record<string, FacultySubmissionRow[]>>({});
    const [rowLoading, setRowLoading] = useState<string | null>(null);
    const [reviewingId, setReviewingId] = useState<string | null>(null);
    const [newActivityOpen, setNewActivityOpen] = useState(false);
    const filePreview = useFilePreview();

    const [expandedFormId, setExpandedFormId] = useState<string | null>(null);
    const [formInnerTab, setFormInnerTab] = useState<'form' | 'responses'>('form');
    const [exportingFormId, setExportingFormId] = useState<string | null>(null);
    const [formDetails, setFormDetails] = useState<Record<string, AssessmentFormDetail | null>>({});
    const [formRowLoading, setFormRowLoading] = useState<string | null>(null);

    // Fetches every batch's activities/forms in one cached call (see
    // FacultyActivitiesData.ts) and filters client-side to this course when
    // embedded in a single batch's page — same cached fan-out serves both
    // the standalone Activities page and every batch's own inner tabs, so
    // switching between batches (or revisiting either) doesn't re-fetch
    // within the cache's TTL.
    const load = async () => {
        try {
            setLoading(true);
            const contactId = getUserDetails()?.ContactId;
            if (!contactId) return;
            const { courses: courseData, activities: allActivities, forms: allForms } = await fetchFacultyActivitiesData(contactId);
            setCourses(courseData);
            if (isScoped) {
                setActivities(allActivities.filter((a) => a.courseOfferingId === courseOfferingId));
                setForms(allForms.filter((f) => f.courseOfferingId === courseOfferingId));
            } else {
                setActivities(allActivities);
                setForms(allForms);
            }
        } catch (error) {
            console.error('Error fetching activities:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseOfferingId]);

    // Distinct trainings (CourseName) across every batch the faculty
    // teaches, sorted A-Z (numeric-aware) so "the first training" is a
    // stable, predictable default — for the standalone (non-scoped) page's
    // Training filter.
    const courseNames = useMemo(
        () => Array.from(new Set(courses.map((c) => c.CourseName).filter(Boolean)))
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })),
        [courses]
    );

    // Once courses load, Training defaults to the first training — there's
    // no "all trainings" option, so one must always be selected (unless
    // already scoped to a single course's own inner tab).
    useEffect(() => {
        if (isScoped || courseNames.length === 0 || selectedCourseName) return;
        setSelectedCourseName(courseNames[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScoped, courseNames]);

    // The batch dropdown only offers batches for the chosen Training,
    // sorted ascending (numeric-aware) so "Batch 2" comes before "Batch 10".
    const coursesForSelectedCourseName = useMemo(
        () => courses
            .filter((c) => c.CourseName === selectedCourseName)
            .sort((a, b) => a.TermName.localeCompare(b.TermName, undefined, { numeric: true, sensitivity: 'base' })),
        [courses, selectedCourseName]
    );

    // Switching Training resets the batch pick back to "All batches" if the
    // current pick no longer belongs to the chosen training, so the batch
    // dropdown never keeps a stale batch from a different training.
    useEffect(() => {
        if (isScoped || selectedCourseOfferingId === 'all') return;
        if (!coursesForSelectedCourseName.some((c) => c.CourseOfferingID === selectedCourseOfferingId)) {
            setSelectedCourseOfferingId('all');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coursesForSelectedCourseName]);

    const visibleActivities = !isScoped && selectedCourseOfferingId !== 'all'
        ? activities.filter((a) => a.courseOfferingId === selectedCourseOfferingId)
        : activities;

    const visibleForms = !isScoped && selectedCourseOfferingId !== 'all'
        ? forms.filter((f) => f.courseOfferingId === selectedCourseOfferingId)
        : forms;

    const preForms = visibleForms.filter((f) => !/post/i.test(f.formType));
    const postForms = visibleForms.filter((f) => /post/i.test(f.formType));
    const formsForTab = activeTab === 'pre' ? preForms : postForms;

    const toggleActivity = async (activity: FacultyActivityItem) => {
        const isOpen = expandedId === activity.id;
        if (isOpen) {
            setExpandedId(null);
            return;
        }
        setExpandedId(activity.id);
        if (submissions[activity.id]) return;
        setRowLoading(activity.id);
        try {
            const subs = await fetchFacultySubmissions(activity.id);
            setSubmissions((prev) => ({ ...prev, [activity.id]: subs }));
        } finally {
            setRowLoading(null);
        }
    };

    const reloadSubmissions = async (activityId: string) => {
        const subs = await fetchFacultySubmissions(activityId);
        setSubmissions((prev) => ({ ...prev, [activityId]: subs }));
    };

    const handleReview = async (activityId: string, submissionId: string, status: 'Approved' | 'Rejected') => {
        const contactId = getUserDetails()?.ContactId;
        if (!contactId) return;
        setReviewingId(submissionId);
        try {
            await reviewSubmission({ contactId, submissionId, status });
            enqueueSnackbar(`Submission marked ${status.toLowerCase()}`, { variant: 'success' });
            await reloadSubmissions(activityId);
        } catch (error) {
            console.error('Error reviewing submission:', error);
            enqueueSnackbar('Unable to update this submission right now.', { variant: 'error' });
        } finally {
            setReviewingId(null);
        }
    };

    const handleViewSubmission = async (submission: FacultySubmissionRow) => {
        try {
            const file = await fetchActivitySubmissionFile(submission);
            if (file) filePreview.show({ buffer: file.buffer, fileName: file.fileName });
        } catch (error) {
            console.error('Error opening submission file:', error);
            enqueueSnackbar('Unable to open this file right now.', { variant: 'error' });
        }
    };

    const handleViewReference = async (activity: FacultyActivityItem) => {
        if (activity.isReferenceLink && activity.referenceUrl) {
            window.open(activity.referenceUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        try {
            const file = await fetchActivityReferenceFile(activity);
            if (file) filePreview.show({ buffer: file.buffer, fileName: file.fileName });
        } catch (error) {
            console.error('Error opening reference document:', error);
            enqueueSnackbar('Unable to open this document right now.', { variant: 'error' });
        }
    };

    const handleExportResponses = async (form: FacultyFormItem) => {
        if (exportingFormId) return;
        setExportingFormId(form.id);
        try {
            const saved = await downloadFormResponsesCsv(form.id, form.name);
            if (!saved) {
                enqueueSnackbar('No responses to export for this form yet.', { variant: 'info' });
            }
        } catch (error) {
            console.error('Error exporting form responses:', error);
            enqueueSnackbar('Unable to export responses right now.', { variant: 'error' });
        } finally {
            setExportingFormId(null);
        }
    };

    const toggleForm = async (form: FacultyFormItem) => {
        const isOpen = expandedFormId === form.id;
        if (isOpen) {
            setExpandedFormId(null);
            return;
        }
        setExpandedFormId(form.id);
        setFormInnerTab('form');
        if (formDetails[form.id]) return;
        const contactId = getUserDetails()?.ContactId;
        if (!contactId) return;
        setFormRowLoading(form.id);
        try {
            const detail = await fetchAssessmentFormDetail(form.id, contactId, 'Faculty');
            setFormDetails((prev) => ({ ...prev, [form.id]: detail }));
        } finally {
            setFormRowLoading(null);
        }
    };

    const batchSelect = !isScoped && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Select
                size="small"
                className={classes.courseSelect}
                value={selectedCourseName}
                onChange={(e) => setSelectedCourseName(e.target.value as string)}
            >
                {courseNames.map((name) => (
                    <MenuItem key={name} value={name}>
                        {name}
                    </MenuItem>
                ))}
            </Select>
            <Select
                size="small"
                className={classes.courseSelect}
                value={selectedCourseOfferingId}
                onChange={(e) => setSelectedCourseOfferingId(e.target.value as string)}
            >
                <MenuItem value="all">All batches</MenuItem>
                {coursesForSelectedCourseName.map((course) => (
                    <MenuItem key={course.CourseOfferingID} value={course.CourseOfferingID}>
                        {course.TermName}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );

    return (
        <Box className={classes.card}>
            {loading && <KenLoader />}
            {!section && <Box className={classes.tabs}>
                <button
                    type="button"
                    className={`${classes.tab} ${activeTab === 'activities' ? 'active' : ''}`}
                    onClick={() => setActiveTab('activities')}
                >
                    Activities
                </button>
                <button
                    type="button"
                    className={`${classes.tab} ${activeTab === 'pre' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pre')}
                >
                    Pre-test
                </button>
                <button
                    type="button"
                    className={`${classes.tab} ${activeTab === 'post' ? 'active' : ''}`}
                    onClick={() => setActiveTab('post')}
                >
                    Post-test
                </button>
            </Box>}

            {activeTab === 'activities' && (
                <>
                    <Box className={classes.sectionHeaderBar}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Activities</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {batchSelect}
                            <KenButton
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon fontSize="small" />}
                                buttonClass={classes.createBtn}
                                onClick={() => setNewActivityOpen(true)}
                            >
                                New activity
                            </KenButton>
                        </Box>
                    </Box>
                    {!loading && visibleActivities.length === 0 && (
                        <Typography className={classes.hint} sx={{ textAlign: 'center', padding: '18px 0' }}>
                            No activities for this batch yet — use New activity to create one.
                        </Typography>
                    )}

                    <Box className={classes.activityList}>
                    {visibleActivities.map((activity) => {
                const isOpen = expandedId === activity.id;
                const subs = submissions[activity.id];
                return (
                    <Box key={activity.id} className={classes.activityCard}>
                        <Box className={classes.activityHeader} onClick={() => toggleActivity(activity)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                                <Box className={classes.activityIcon}>
                                    <DescriptionOutlinedIcon fontSize="small" />
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{activity.name}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                <Typography sx={{ fontSize: '13px', color: '#8A94A4' }}>{activity.batchName}</Typography>
                                <span className={classes.statusPill} style={submissionStatusPillStyle(activity.submittedCount > 0 ? 'Approved' : 'Pending')}>
                                    {activity.submittedCount} / {activity.totalEnrolledCount} submitted
                                </span>
                                {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>
                        </Box>
                        <Collapse in={isOpen}>
                            <Box sx={{ padding: '0 20px 18px' }}>
                                {activity.description && (
                                    <Typography sx={{ fontSize: '13px', color: '#6B7280', margin: '6px 0 12px' }}>
                                        {activity.description}
                                        {activity.dueDate ? ` · due ${activity.dueDate}` : ''}
                                    </Typography>
                                )}
                                {(activity.referenceContentVersionId || activity.referenceUrl) && (
                                    <Box
                                        onClick={() => handleViewReference(activity)}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '8px 12px',
                                            marginBottom: '14px',
                                            borderRadius: '10px',
                                            background: '#F5F7FB',
                                            border: '1px solid #E5EAF2',
                                            cursor: 'pointer',
                                            transition: 'background 0.15s ease',
                                            '&:hover': { background: '#ECEFF5' },
                                        }}
                                    >
                                        <InsertDriveFileOutlinedIcon sx={{ fontSize: '18px', color: '#1B7EC2' }} />
                                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#212B38' }}>
                                            {activity.referenceFileName || 'Reference document'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#1B7EC2', fontWeight: 600, marginLeft: '4px' }}>
                                            View
                                        </Typography>
                                    </Box>
                                )}
                                {rowLoading === activity.id && <KenLoader />}
                                {rowLoading !== activity.id && (
                                    subs && subs.length > 0 ? (
                                        <table className={classes.table}>
                                            <thead>
                                                <tr>
                                                    <th className={classes.th}>Student</th>
                                                    <th className={classes.th}>Status</th>
                                                    <th className={classes.th}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {subs.map((row) => (
                                                    <tr key={row.submissionId}>
                                                        <td className={classes.td} style={{ fontWeight: 500 }}>{row.contactName}</td>
                                                        <td className={classes.td}>
                                                            <span className={classes.statusPill} style={submissionStatusPillStyle(row.status)}>
                                                                {row.status}
                                                            </span>
                                                            {row.feedback && (
                                                                <Typography sx={{ fontSize: '12px', color: '#8A94A4', marginTop: '4px' }}>{row.feedback}</Typography>
                                                            )}
                                                        </td>
                                                        <td className={classes.td} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                            {row.contentVersionId && (
                                                                <KenButton
                                                                    variant="outlined"
                                                                    size="small"
                                                                    sx={{ textTransform: 'none', borderRadius: '8px', marginRight: '8px' }}
                                                                    onClick={() => handleViewSubmission(row)}
                                                                >
                                                                    View
                                                                </KenButton>
                                                            )}
                                                            {row.status !== 'Approved' && row.status !== 'Rejected' && (
                                                                <>
                                                                    <KenButton
                                                                        variant="outlined"
                                                                        size="small"
                                                                        disabled={reviewingId === row.submissionId}
                                                                        sx={{ textTransform: 'none', borderRadius: '8px', marginRight: '8px' }}
                                                                        onClick={() => handleReview(activity.id, row.submissionId, 'Approved')}
                                                                    >
                                                                        Approve
                                                                    </KenButton>
                                                                    <KenButton
                                                                        variant="outlined"
                                                                        size="small"
                                                                        disabled={reviewingId === row.submissionId}
                                                                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                                                                        onClick={() => handleReview(activity.id, row.submissionId, 'Rejected')}
                                                                    >
                                                                        Reject
                                                                    </KenButton>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <Typography className={classes.hint} sx={{ padding: '10px 0 0' }}>No submission records found for this activity.</Typography>
                                    )
                                )}
                            </Box>
                        </Collapse>
                    </Box>
                );
            })}
                    </Box>
                </>
            )}

            {(activeTab === 'pre' || activeTab === 'post') && (
                <>
                    <Box className={classes.sectionHeaderBar}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                            {activeTab === 'pre' ? 'Pre-test' : 'Post-test'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {batchSelect}
                            <KenButton
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon fontSize="small" />}
                                buttonClass={classes.createBtn}
                                onClick={() => navigate(routes.AssessmentCreate)}
                            >
                                New form
                            </KenButton>
                        </Box>
                    </Box>

                    {!loading && formsForTab.length === 0 && (
                        <Typography className={classes.hint} sx={{ textAlign: 'center', padding: '18px 0' }}>
                            No {activeTab === 'pre' ? 'pre-test' : 'post-test'} forms for this batch yet — use New form to create one.
                        </Typography>
                    )}

                    <Box className={classes.activityList}>
                    {formsForTab.map((form) => {
                        const isPostTest = /post/i.test(form.formType);
                        const isOpen = expandedFormId === form.id;
                        const detail = formDetails[form.id];
                        return (
                            <Box key={form.id} className={classes.activityCard}>
                                <Box className={classes.activityHeader} onClick={() => toggleForm(form)}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                                        <Box className={classes.activityIcon} sx={{ background: isPostTest ? '#E8F5EC' : '#E9F2FB', color: isPostTest ? '#3AA35B' : '#2380C4' }}>
                                            <DescriptionOutlinedIcon fontSize="small" />
                                        </Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{form.name}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                        <span className={classes.formPill} style={formPillStyle(form.formType)}>{form.formType}</span>
                                        <span className={classes.formPill} style={publishedPillStyle(form.published)}>
                                            {form.published ? 'Published' : 'Draft'}
                                        </span>
                                        <Typography sx={{ fontSize: '13px', color: '#8A94A4' }}>
                                            {form.respondedCount} / {form.enrolledCount} responded
                                        </Typography>
                                        {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </Box>
                                </Box>
                                <Collapse in={isOpen}>
                                    <Box sx={{ padding: '0 20px 18px' }}>
                                        <Box className={classes.tabs} sx={{ marginBottom: '12px' }}>
                                            <button
                                                type="button"
                                                className={`${classes.tab} ${formInnerTab === 'form' ? 'active' : ''}`}
                                                onClick={() => setFormInnerTab('form')}
                                            >
                                                Form
                                            </button>
                                            <button
                                                type="button"
                                                className={`${classes.tab} ${formInnerTab === 'responses' ? 'active' : ''}`}
                                                onClick={() => setFormInnerTab('responses')}
                                            >
                                                Responses ({form.respondedCount})
                                            </button>
                                            {/* Faculty CSV of every student's answers — one row per
                                                respondent, one column per question. Nothing to export
                                                until at least one person has responded. */}
                                            <KenButton
                                                variant="text"
                                                size="small"
                                                startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '18px !important' }} />}
                                                sx={{
                                                    ...viewResponseButtonSx,
                                                    marginLeft: 'auto',
                                                    border: 'none',
                                                    backgroundColor: 'transparent',
                                                    '&:hover': { border: 'none', backgroundColor: '#F4F7FB' },
                                                }}
                                                disabled={form.respondedCount === 0 || exportingFormId === form.id}
                                                onClick={() => handleExportResponses(form)}
                                            >
                                                {exportingFormId === form.id ? 'Exporting…' : 'Export responses'}
                                            </KenButton>
                                        </Box>

                                        {formRowLoading === form.id && <KenLoader />}

                                        {formRowLoading !== form.id && formInnerTab === 'form' && (
                                            <>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                                    <Typography sx={{ fontSize: '13px', color: '#8A94A4' }}>
                                                        {detail?.questions.length ?? 0} questions &middot; {form.formType} &middot; not scored
                                                    </Typography>
                                                    <KenButton
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<EditIcon fontSize="small" />}
                                                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                                                        onClick={() => navigate(routes.AssessmentView.replace(':id', String(form.id)))}
                                                    >
                                                        Edit form
                                                    </KenButton>
                                                </Box>
                                                {(detail?.questions ?? []).map((question, qIndex) => (
                                                    <Box key={question.questionId} sx={{ marginBottom: '18px' }}>
                                                        <Typography sx={{ fontSize: '14px' }}>
                                                            {qIndex + 1}. {question.questionText}
                                                        </Typography>
                                                        {question.answerType === 'Open Text' ? (
                                                            <Box sx={{ border: '1px solid #ECEFF5', borderRadius: '8px', height: '38px', marginTop: '8px', background: '#F9FAFB' }} />
                                                        ) : (
                                                            <Box sx={{ marginTop: '8px' }}>
                                                                {question.options.map((option) => (
                                                                    <Box key={option.optionId} sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                                                        <Box sx={{ width: 16, height: 16, border: '1.5px solid #B4BCCA', borderRadius: '4px', flexShrink: 0 }} />
                                                                        <Typography sx={{ fontSize: '13.5px' }}>{option.optionText}</Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ))}
                                                {(!detail || detail.questions.length === 0) && (
                                                    <Typography className={classes.hint}>No questions added to this form yet.</Typography>
                                                )}
                                            </>
                                        )}

                                        {formRowLoading !== form.id && formInnerTab === 'responses' && (
                                            <>
                                                {(!detail?.responses || detail.responses.length === 0) && (
                                                    <Typography className={classes.hint} sx={{ textAlign: 'center', padding: '24px 0' }}>
                                                        No responses yet for this form.
                                                    </Typography>
                                                )}
                                                {detail?.responses && detail.responses.length > 0 && (
                                                    <>
                                                        <Typography sx={{ fontSize: '13px', color: '#8A94A4', marginBottom: '14px' }}>
                                                            {form.respondedCount} of {form.enrolledCount} responded &middot; open a learner's response to read their answers &middot; not scored
                                                        </Typography>
                                                        {detail.responses.map((respondent) => (
                                                            <Box
                                                                key={respondent.responseId}
                                                                sx={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '13px 0', borderTop: '1px solid #ECEFF5', '&:first-of-type': { borderTop: 'none' } }}
                                                            >
                                                                <Box
                                                                    sx={{ width: 36, height: 36, borderRadius: '50%', background: '#F5F7FB', color: '#8A94A4', display: 'grid', placeItems: 'center', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}
                                                                >
                                                                    {respondent.respondentName.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?'}
                                                                </Box>
                                                                <Typography sx={{ fontWeight: 600, fontSize: '14px', flex: 1 }}>{respondent.respondentName}</Typography>
                                                                {/* The row already means "this learner responded" — the
                                                                    form's own Completed/Published state is shown up in the
                                                                    header, so the pill here reads Responded. */}
                                                                <span className={classes.respondedPill}>Responded</span>
                                                                <KenButton
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={<VisibilityOutlinedIcon sx={{ fontSize: '18px !important' }} />}
                                                                    sx={viewResponseButtonSx}
                                                                    onClick={() => navigate(routes.AssessmentResponse.replace(':responseId', respondent.responseId))}
                                                                >
                                                                    View response
                                                                </KenButton>
                                                            </Box>
                                                        ))}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })}
                    </Box>
                </>
            )}

            <NewActivityDialog
                open={newActivityOpen}
                courses={courseOfferingId ? courses.filter((c) => c.CourseOfferingID === courseOfferingId) : courses}
                defaultCourseOfferingId={courseOfferingId}
                onClose={() => setNewActivityOpen(false)}
                onCreated={() => {
                    const contactId = getUserDetails()?.ContactId;
                    if (contactId) invalidateFacultyActivitiesCache(contactId);
                    load();
                }}
            />

            <FilePreviewDialog
                open={filePreview.open}
                url={filePreview.url}
                fileName={filePreview.fileName}
                mimeType={filePreview.mimeType}
                onClose={filePreview.close}
            />
        </Box>
    );
};

export default FacultyActivitiesList;
